import Highcharts from "highcharts";

import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { PieSeriesAdapter } from "@/utils/chart/adapters/series/pie-chart-series-adapter";
import { ICategorizedSerieAdapter } from "@/utils/chart/adapters/types/categorized-chart-adapter.type";
import { getMaybeTruncatedNumber } from "@/utils/number";
import { EPrefixSymbol } from "@/utils/translation-utils";

export interface IPieChartSpecs<KIND extends string> {
  seriesAdapter: ReadonlyArray<[KIND, ICategorizedSerieAdapter<KIND>]>;
  isDonut: boolean;
  showTotal: boolean;
  prefixSymbol: EPrefixSymbol | undefined;
  valueSuffix: string;
  colors: (string | Highcharts.PatternObject)[];
  accessibilityDescription: string;
  legendWidth: number;
}

export const pieChartDefaultColors = ["#B652F4", "#2582FE", "#37CEFF", "#00F3D7"];

export const pieChartGreyColor = "#D9D9D9";

export const grayStrokePattern: Highcharts.PatternObject = {
  pattern: {
    id: "0",
    path: {
      d: "M 10 0 L 0 10 M -5 5 L 5 15 M 15 -5 L 25 5",
      strokeWidth: 1,
    },
    width: 10,
    height: 10,
    backgroundColor: "transparent",
    color: "gray",
  },
};

export function buildPieChartOptions<KIND extends string>(
  specs: Partial<IPieChartSpecs<KIND>>,
  serie: TCategorizedDataResponseSerie<KIND>,
): Highcharts.Options {
  const unit = serie.unit;
  const kind = serie.kind;

  const maybeAdapterTuple = specs.seriesAdapter?.find(([k]) => k === kind);
  const adapter = maybeAdapterTuple ? maybeAdapterTuple[1] : PieSeriesAdapter();

  const seriesOpts: Highcharts.SeriesOptionsType[] = adapter.adapt(serie);

  return {
    chart: {
      type: "pie",
      events: {
        render: function () {
          if (!specs.showTotal) return;

          const data = this.series[0].data;
          let sum = 0;

          for (let i = 0; i < data.length; i++) {
            if (data[i].visible) {
              sum += data[i].y ?? 0;
            }
          }

          this.setTitle({
            text: `
              <h1 class="text-black">${getMaybeTruncatedNumber(sum)}</h1>
              <span>${specs.valueSuffix ?? `${specs.prefixSymbol ?? ""}${unit}`}</span>
            `,
            floating: true,
            useHTML: true,
            verticalAlign: "middle",
            x: -75,
          });
        },
      },
    },
    title: {
      text: "",
    },
    legend: {
      enabled: true,
      labelFormat: `
        <div class="flex flex-col items-center">
          <div>
            <span class="text-secondary">{name}</span>
            <h5>{point.percentage:.1f}%</h5>
          </div>
        </div>
      `,
      useHTML: true,
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      width: specs.legendWidth,
    },
    tooltip: {
      valueSuffix: specs.valueSuffix ?? ` ${specs.prefixSymbol ?? ""}${unit}`,
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
      pie: specs.isDonut
        ? {
            size: "100%",
            innerSize: "80%",
            borderWidth: 5,
            borderRadius: 5,
          }
        : {},
    },
    colors: specs.colors ?? pieChartDefaultColors,
    credits: {
      enabled: false,
    },
    series: seriesOpts,
    accessibility: {
      description: specs.accessibilityDescription,
    },
  };
}
